import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { motion, useInView } from "framer-motion";
import {
  Trash2, Check, ArrowRight, ArrowLeft, Plus, Minus,
  Calendar as CalendarIcon, Phone, Droplet, Zap, Info, Clock,
} from "lucide-react";
import { BUSINESS, BIN_FREQUENCIES, BIN_FAQS } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PhotoCapture from "@/components/PhotoCapture";

const TIME_SLOTS = [
  "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function BinCleaning() {
  const [frequency, setFrequency] = useState("monthly");
  const [trashBins, setTrashBins] = useState(2);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slotError, setSlotError] = useState("");
  const [photo, setPhoto] = useState(null); // optional quick photo of the bin(s)

  const currentFreq = BIN_FREQUENCIES.find((f) => f.id === frequency);

  const fetchAvailability = useCallback((d) => {
    if (!d) { setSlots(null); return; }
    api
      .get("/availability", { params: { date: format(d, "yyyy-MM-dd"), booking_type: "bin" } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots(null));
  }, []);

  useEffect(() => {
    fetchAvailability(date);
  }, [date, fetchAvailability]);

  useEffect(() => {
    if (!slots || !time) return;
    const s = slots.find((x) => x.time === time);
    if (s && !s.available) {
      setTime("");
      setSlotError("That time is no longer available — please choose another.");
    }
  }, [slots, time]);
  const total = useMemo(
    () => (currentFreq ? currentFreq.price * trashBins : 0),
    [currentFreq, trashBins]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (trashBins < 1) return toast.error("Add at least 1 bin.");
    if (!date) return toast.error("Pick a date.");
    if (!time) return toast.error("Pick a time.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        booking_type: "bin",
        frequency,
        trash_bins: trashBins,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        photo_url: photo?.status === "ready" ? photo.url : undefined,
      });
      setDone(data);
      toast.success("Booking request received.");
    } catch (err) {
      if (err.response?.status === 409) {
        const msg = formatApiErrorDetail(err.response.data?.detail);
        setSlotError(msg);
        setTime("");
        toast.error(msg);
        fetchAvailability(date);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit booking.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen pt-32 pb-24 px-5 lg:px-8 bg-[#050914]">
        <div className="max-w-2xl mx-auto text-center" data-testid="bin-booking-success">
          <div className="h-20 w-20 mx-auto rounded-full bg-blue-600/20 border border-blue-500/40 grid place-items-center mb-6">
            <Check className="h-10 w-10 text-blue-400" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Booking Request Received.</h1>
          <p className="mt-4 text-white/70">
            Thanks <span className="text-white font-semibold">{done.name}</span> — we have your request for{" "}
            <span className="text-blue-400 font-semibold">{done.trash_bins} bin(s) · {BIN_FREQUENCIES.find((f) => f.id === done.frequency)?.label}</span>{" "}
            on <span className="text-white">{done.preferred_date}</span> at{" "}
            <span className="text-white">{done.preferred_time}</span>.
          </p>
          <p className="mt-2 text-white/60">Total: <span className="text-white font-semibold">${done.estimated_price}</span></p>
          <p className="mt-4 text-white/60 text-sm">
            <strong className="text-white">What happens next:</strong> we'll review your request and contact you at {done.phone} to confirm.
            Your appointment is not confirmed until we reach out.
          </p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-md font-semibold transition-all">
            Back to Home <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#050914] text-white">
      {/* HERO */}
      <section className="relative pt-32 pb-16 px-5 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#050914]" />
        <div className="relative max-w-5xl mx-auto">
          <FadeIn>
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6">
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[10px] uppercase tracking-[0.25em] text-blue-300 mb-4">
              <Trash2 className="h-3 w-3" /> Trash Bin Cleaning
            </div>
            <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tighter leading-[0.95]">
              Fresh, sanitized<br /><span className="metallic-text">trash bins.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
              Hot-water pressure wash. Professional sanitizer. Fresh finish. Choose the frequency that works for you.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* PRICING — THREE FREQUENCY OPTIONS */}
      <section id="bin-tiers" className="py-16 px-5 lg:px-8 border-y border-white/5 bg-[#070c1c]">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-3">Choose Frequency</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Simple, transparent pricing.</h2>
              <p className="mt-3 text-white/60">Per bin. No contracts. No surprises.</p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-5">
            {BIN_FREQUENCIES.map((f, i) => {
              const isSelected = frequency === f.id;
              return (
                <FadeIn key={f.id} delay={i * 0.06}>
                  <button
                    type="button" onClick={() => setFrequency(f.id)}
                    className={`w-full text-left rounded-2xl p-6 h-full transition-all border-2 relative ${
                      isSelected
                        ? "border-blue-500 bg-blue-500/10 shadow-2xl shadow-blue-600/20"
                        : "border-white/10 bg-[#0B132B] hover:border-white/20"
                    }`}
                    data-testid={`bin-freq-${f.id}`}
                  >
                    {f.badge && (
                      <div className="absolute -top-3 right-4 bg-emerald-500 text-white text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                        {f.badge}
                      </div>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-blue-400">{f.label}</div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-white">${f.price}</span>
                      <span className="text-xs text-white/50">per bin</span>
                    </div>
                    <p className="text-sm text-white/60 mt-3 leading-relaxed">{f.desc}</p>
                    <div className={`mt-5 inline-flex items-center gap-2 font-semibold text-sm px-4 py-2 rounded-md ${
                      isSelected ? "bg-blue-600 text-white" : "bg-white/10 text-white/80"
                    }`}>
                      {isSelected ? "Selected" : `Select`}
                      {isSelected && <Check className="h-3.5 w-3.5" />}
                    </div>
                  </button>
                </FadeIn>
              );
            })}
          </div>

          <div className="mt-6 max-w-3xl mx-auto flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
            <Info className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
            <span>Example: 19 bins × Bi-Weekly ($15/bin) = <strong className="text-white">$285 per service</strong>, approximately every 2 weeks.</span>
          </div>
        </div>
      </section>

      {/* CUSTOMER REQUIREMENTS — HOOKUPS */}
      <section className="py-14 px-5 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <div className="rounded-2xl border-2 border-amber-500/30 bg-amber-500/5 p-6 sm:p-8">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400 mb-2">Customer Requirements</div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white">Before your appointment</h3>
              <p className="mt-3 text-white/70 leading-relaxed">
                For the time being, Authority Detailing Co. requires access to a water hookup and electrical outlet
                at the service location. Please make sure both are accessible before your scheduled service.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/10 p-4">
                  <Droplet className="h-5 w-5 text-blue-400" />
                  <div>
                    <div className="text-white font-semibold text-sm">Water Hookup</div>
                    <div className="text-xs text-white/50">A usable water connection.</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/10 p-4">
                  <Zap className="h-5 w-5 text-blue-400" />
                  <div>
                    <div className="text-white font-semibold text-sm">Electrical Hookup</div>
                    <div className="text-xs text-white/50">A usable electrical outlet.</div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 px-5 lg:px-8 bg-[#070c1c]">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <div className="text-center mb-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-3">FAQ</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Common Questions</h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {BIN_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`b-${i}`} className="rounded-xl border border-white/10 bg-[#0B132B] px-5" data-testid={`bin-faq-${i}`}>
                  <AccordionTrigger className="text-white text-left text-base font-semibold hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/70 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
        </div>
      </section>

      {/* BOOK */}
      <section id="bin-book" className="py-16 px-5 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-3">Book Now</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Schedule Your Service</h2>
              <p className="mt-3 text-white/60">
                Selected: <span className="text-white font-semibold">{currentFreq?.label}</span> · <span className="text-white/80">${currentFreq?.price}/bin</span>
              </p>
            </div>
          </FadeIn>

          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <form onSubmit={submit} className="rounded-2xl glass p-6 sm:p-8 space-y-6">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">How Many Bins?</div>
                <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-2">
                  <button type="button" onClick={() => setTrashBins((n) => Math.max(1, n - 1))}
                    className="h-10 w-10 grid place-items-center rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30"
                    disabled={trashBins <= 1} data-testid="bin-decrement" aria-label="Decrease">
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="min-w-[80px] text-center" data-testid="bin-count">
                    <div className="text-2xl font-bold">{trashBins}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">bins</div>
                  </div>
                  <button type="button" onClick={() => setTrashBins((n) => Math.min(100, n + 1))}
                    className="h-10 w-10 grid place-items-center rounded-md bg-blue-600 hover:bg-blue-500"
                    data-testid="bin-increment" aria-label="Add">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Date & Time</div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="w-full sm:w-72 text-left flex items-center gap-3 bg-white/5 border border-white/10 rounded-md h-12 px-4 hover:border-white/20" data-testid="bin-date-input">
                      <CalendarIcon className="h-4 w-4 text-blue-400" />
                      {date ? format(date, "EEE, MMM d, yyyy") : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0B132B] border-white/10">
                    <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setSlotError(""); }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                  </PopoverContent>
                </Popover>

                <div className="mt-4">
                  {!date ? (
                    <div className="rounded-xl border border-dashed border-white/15 bg-white/5 h-12 flex items-center gap-2 px-4 text-sm text-white/40" data-testid="bin-time-select">
                      <Clock className="h-4 w-4" /> Pick a date to see live availability
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="bin-time-select">
                      {TIME_SLOTS.map((t) => {
                        const s = slots?.find((x) => x.time === t);
                        const unavailable = slots ? !s?.available : false;
                        const active = time === t;
                        return (
                          <button
                            type="button" key={t}
                            disabled={unavailable}
                            onClick={() => { setTime(t); setSlotError(""); }}
                            title={unavailable ? (s?.reason === "taken" ? "Already booked" : "Unavailable") : `Arrive at ${t}`}
                            className={`h-12 rounded-lg border text-sm font-semibold transition-all ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                : unavailable
                                  ? "border-white/5 bg-white/[0.02] text-white/25 line-through cursor-not-allowed"
                                  : "border-white/10 bg-white/5 text-white/80 hover:border-blue-500/50 hover:text-white"
                            }`}
                            data-testid={`bin-slot-${t.replace(/[: ]/g, "-").toLowerCase()}`}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {slotError && (
                    <p className="mt-3 text-sm text-red-400 font-medium" data-testid="bin-slot-error">{slotError}</p>
                  )}
                  <p className="mt-3 text-xs text-white/40">
                    Times are arrival windows. Your appointment is confirmed when we contact you — submitting this form sends a request.
                  </p>
                </div>
              </div>

              <div>
                <PhotoCapture
                  idPrefix="bin"
                  heading="Send us a quick photo of your bin."
                  subtext="Upload a photo so we can get a better idea of what we're servicing and help us prepare."
                  value={photo}
                  onChange={setPhoto}
                />
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400 mb-2">Your Details</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="bg-white/5 border-white/10 h-12" data-testid="bin-name" />
                  <Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="bg-white/5 border-white/10 h-12" data-testid="bin-phone" />
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-white/5 border-white/10 h-12 sm:col-span-2" data-testid="bin-email" />
                  <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Service Address" className="bg-white/5 border-white/10 h-12 sm:col-span-2" data-testid="bin-address" />
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (gate codes, bin location...)" rows={3} className="bg-white/5 border-white/10 sm:col-span-2" data-testid="bin-notes" />
                </div>
              </div>

              <button type="submit" disabled={submitting || photo?.status === "uploading"}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold py-4 rounded-md shadow-lg shadow-blue-600/30 transition-all"
                data-testid="bin-submit-btn">
                {submitting ? "Submitting..." : photo?.status === "uploading" ? "Attaching photo…" : "Book Your Service"}
              </button>
            </form>

            <aside className="lg:sticky lg:top-28 self-start rounded-2xl glass p-6 h-fit space-y-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-blue-400">Estimate</div>
              <div className="text-white">
                <div className="font-display text-lg font-semibold leading-tight">{currentFreq?.label}</div>
                <div className="text-xs text-white/60">${currentFreq?.price} per bin</div>
              </div>
              <div className="border-t border-white/10 pt-4 text-sm space-y-1.5">
                <div className="flex justify-between text-white/70"><span>Bins</span><span>{trashBins}</span></div>
                <div className="flex justify-between text-white/70"><span>Rate</span><span>${currentFreq?.price}/bin</span></div>
              </div>
              <div className="border-t border-white/10 pt-4">
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Total</div>
                <div className="font-display text-4xl font-bold mt-1" data-testid="bin-estimate">${total}</div>
                <div className="text-[11px] text-white/40 mt-2">
                  {frequency === "bi_weekly" ? "Per Bi-Weekly service (~every 2 weeks)" : frequency === "monthly" ? "Per Monthly service" : "One-time cleaning"}
                </div>
              </div>
              <div className="border-t border-white/10 pt-4 text-xs text-white/60">
                Questions? Call <a href={`tel:${BUSINESS.phoneRaw}`} className="text-blue-400 font-semibold">{BUSINESS.phone}</a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
